/* eslint-disable no-console */
import { waitForSessionSync } from '../../dcx/cop/components/SilentTokenProvider/sessionSync'
import { getPermissionsUrl } from '../../dcx/cop/constants/service-endpoints'
import { get } from '../../dcx/cop/services/http.service'

const PERMISSIONS_CACHE_KEY = 'cop:permissions'
const CURRENT_USER_SESSION_KEY = 'cop:current-user'
const CUSTOMER_ID_SESSION_KEY = 'cop:customer-id'
const CUSTOMER_READY_EVENT = 'cop:customer-ready'
const POWERPORTAL_MAIN_SELECTOR = '.cmp-powerportal-main'
// Classes are suffixed per content type (e.g. cmp-navigation__item--cop-subscriptions),
// so match the prefix via an attribute selector rather than an exact class token.
const L0_ITEM_SELECTOR = `${POWERPORTAL_MAIN_SELECTOR} .cmp-navigation__item--level-0[class*="cmp-navigation__item--cop"]`
const SWITCH_ACCOUNT_SELECTOR = '.cmp-header__top-item.cmp-header__switch-account'

const NAV_TYPE_CLASS_PREFIX = 'cmp-navigation__item--cop-'
const NAV_TYPE_PERMISSION_MAP = {
  'cases':       'Cases.ViewList',
  'agreements':  'Agreements.ViewList',
  'assets':      'Assets.ViewList',
  'sites':       'Sites.View',
  'news & events': 'News.View',
  'resources':   'Resources.View',
}

// Nav item gated by role (cop:current-user) rather than the permissions API
const SUBSCRIPTIONS_CLASS = 'cmp-navigation__item--cop-subscriptions'

// Maps URL path prefix → permission required to access that page directly
const PAGE_PATH_PERMISSION_MAP = {
  '/cases':      'Cases.ViewList',
  '/agreements': 'Agreements.ViewList',
  '/assets':     'Assets.ViewList',
  '/sites':      'Sites.View',
}

const getHomePath = () =>
    document.querySelector('meta[name="cop-home-path"]')?.getAttribute('content') ?? '/'

const enforcPageAccess = permissionSet => {
  const path = window.location.pathname.replace(/\.html$/i, '')
  const required = Object.entries(PAGE_PATH_PERMISSION_MAP).find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1]
  if (required && !permissionSet.has(required)) {
    window.location.assign(getHomePath())
  }
}

const fetchPermissionsFromApi = async customerId => {
  return get(getPermissionsUrl(customerId))
}

const buildPermissionSetFromCache = () => {
  try {
    const raw = sessionStorage.getItem(PERMISSIONS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed?.permissions)) return null
    return new Set(parsed.permissions.map(p => p.name))
  } catch {
    return null
  }
}

const getCurrentUserRoles = () => {
  try {
    const raw = sessionStorage.getItem(CURRENT_USER_SESSION_KEY)
    if (!raw) return { isSystemAdmin: false, isUserManager: false }
    const user = JSON.parse(raw)
    return { isSystemAdmin: user.isSystemAdmin === true, isUserManager: user.isUserManager === true }
  } catch {
    return { isSystemAdmin: false, isUserManager: false }
  }
}

const hideAllL0Items = () => {
  document.querySelectorAll(L0_ITEM_SELECTOR).forEach(el => { el.style.display = 'none' })
  setSwitchAccountVisible(false)
}

// Fail-open: restores all items when API errors or customer-id is unavailable
const showAllL0Items = () => {
  document.querySelectorAll(L0_ITEM_SELECTOR).forEach(el => { el.style.display = '' })
  setSwitchAccountVisible(true)
}

const setSwitchAccountVisible = visible => {
  document.querySelectorAll(SWITCH_ACCOUNT_SELECTOR).forEach(el => {
    el.style.display = visible ? '' : 'none'
  })
}

// Collects the permissions required by an item based on its cop-<type> modifier classes.
// An item may carry several content-type classes; it is permitted if ANY of them is granted.
const getRequiredPermissionsForItem = el => {
  const required = []
  el.classList.forEach(cls => {
    if (cls.startsWith(NAV_TYPE_CLASS_PREFIX)) {
      const permission = NAV_TYPE_PERMISSION_MAP[cls.slice(NAV_TYPE_CLASS_PREFIX.length)]
      if (permission) required.push(permission)
    }
  })
  return required
}

const showPermittedItems = permissionSet => {
  const roles = getCurrentUserRoles()
  let isSubscriptionsVisible = false
  document.querySelectorAll(L0_ITEM_SELECTOR).forEach(el => {
    if (el.classList.contains(SUBSCRIPTIONS_CLASS)) {
      if (roles.isSystemAdmin) { isSubscriptionsVisible = true; el.style.display = '' }
      return
    }
    const required = getRequiredPermissionsForItem(el)
    // Ungated items (no cop-<type> class) stay visible; gated items need a matching permission
    const visible = required.length === 0 || required.some(p => permissionSet.has(p))
    el.style.display = visible ? '' : 'none'
  })
  // Enable switch account when at least one gated nav permission is granted
  const hasAnyNavPermission = Object.values(NAV_TYPE_PERMISSION_MAP).some(p => permissionSet.has(p))
  setSwitchAccountVisible(hasAnyNavPermission || isSubscriptionsVisible)
}

const fetchAndApply = async customerId => {
  // Ensure we are on a PowerPortal page before touching the nav
  if (!document.querySelector(POWERPORTAL_MAIN_SELECTOR)) return
  try {
    const data = await fetchPermissionsFromApi(customerId)
    try { sessionStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify(data)) } catch { /* non-fatal */ }
    const permissionSet = buildPermissionSetFromCache()
    if (permissionSet) {
      enforcPageAccess(permissionSet)
      showPermittedItems(permissionSet)
    } else {
      showAllL0Items()
    }
  } catch (err) {
    console.error('[NavPermissions] PermissionsV2 API error, showing all items:', err)
    showAllL0Items()
  }
}

// Fires on every customer selection (first login + switch account)
window.addEventListener(CUSTOMER_READY_EVENT, async event => {
  if (!document.querySelector(POWERPORTAL_MAIN_SELECTOR)) return
  const customerId = event.detail?.customerId
  if (!customerId) { showAllL0Items(); return }
  hideAllL0Items()
  await fetchAndApply(customerId)
})

/**
 * Called from navigation.js when .cmp-powerportal-main is detected.
 * Scenarios:
 *  A. Cached permissions → apply immediately
 *  B. customer-id in sessionStorage → fetch now
 *  C. customer-id not yet set → module-level listener handles it after login
 */
export const initNavPermissions = async () => {
  if (!document.querySelector(POWERPORTAL_MAIN_SELECTOR)) return

  hideAllL0Items()

  // New tab has empty sessionStorage — give an existing tab a chance to hand off
  // customer-id/permissions via BroadcastChannel before falling back to Scenario C
  await waitForSessionSync()

  const cachedPermissionSet = buildPermissionSetFromCache()
  if (cachedPermissionSet) {
    enforcPageAccess(cachedPermissionSet)
    showPermittedItems(cachedPermissionSet)
    return
  }

  const existingCustomerId = sessionStorage.getItem(CUSTOMER_ID_SESSION_KEY)
  if (existingCustomerId) {
    await fetchAndApply(existingCustomerId)
  }
  // Scenario C: items stay hidden until cop:customer-ready fires
}
